import { get_token } from "@/router";
import {
    build_user_payload,
    extract_collection,
    get_auth_headers,
    get_error_message,
    get_full_name,
    get_initials,
    get_response_data,
    get_role_id,
    get_role_name,
    get_status_endpoint,
    get_user_profile_data,
    get_user_role,
    get_user_role_id,
    get_user_row
} from "@/components/SettingsUserManagementTab/settings_user_management_data";

function get_table_helper() {
    return window.global_data_table || null;
}

async function send_request(request) {
    const http_client = window.fetch.bind(window);
    return http_client(request);
}

function is_matching_user_id(component, user) {
    const user_id = user?.id || null;
    return Boolean(component.current_user_id && String(user_id) === String(component.current_user_id));
}

function is_matching_user_email(component, user) {
    const user_email = String(user?.email || "").toLowerCase();
    return Boolean(component.current_user_email && user_email === component.current_user_email);
}

export const settings_user_table_helpers = {
    watch: {},
    methods: {
        initialize_tables_when_ready() {
            this.$nextTick(() => {
                get_table_helper()?.init_all_tables?.(this.$el, ".global_data_table");
            });
        },

        destroy_tables() {
            get_table_helper()?.destroy_all_tables?.(this.$el, ".global_data_table");
        },

        rebuild_tables() {
            this.$nextTick(() => {
                get_table_helper()?.rebuild_all_tables?.(this.$el, ".global_data_table");
            });
        }
    }
};

export default {
    get_auth_headers,
    get_role_name,
    get_role_id,
    get_user_role_id,
    get_user_role,
    get_user_profile_data,
    extract_collection,
    get_error_message,
    get_response_data,
    get_user_row,
    get_full_name,
    get_initials,

    async load_initial_data() {
        await this.load_roles();
        await this.load_users();
    },

    async load_roles() {
        this.is_loading_roles = true;

        try {
            const roles = await this.fetch_role_options();
            this.role_options = roles.length ? roles : this.get_fallback_role_options();
        } catch {
            this.role_options = this.get_fallback_role_options();
        } finally {
            this.is_loading_roles = false;
        }
    },

    async fetch_role_options() {
        if (!this.roles_url || !get_token()) {
            if (!this.roles_url) console.warn("VUE_APP_ROLES_URL is not configured.");
            return [];
        }

        const request = new Request(this.roles_url, {
            method: "GET",
            headers: get_auth_headers(false)
        });
        const response = await send_request(request);
        const roles_data = await get_response_data(response, "Unable to load roles.");

        return extract_collection(roles_data, ["data", "results", "roles"]);
    },

    async load_users() {
        this.is_loading = true;
        this.load_error = "";

        try {
            const users = await this.fetch_user_rows();
            this.set_users(users.length ? users : this.get_fallback_users(), false);
        } catch (error) {
            this.set_users(this.get_fallback_users(), false);
            this.load_error = `${error.message || "Unable to load users from the server."} Showing available local profile data.`;
        } finally {
            this.is_loading = false;
            this.$nextTick(() => this.rebuild_tables());
        }
    },

    async fetch_user_rows() {
        if (!this.users_list_url || !get_token()) {
            this.load_error = this.get_missing_user_list_message();
            return [];
        }

        const request = new Request(this.users_list_url, {
            method: "GET",
            headers: get_auth_headers(false)
        });
        const response = await send_request(request);
        const users_data = await get_response_data(response, "Unable to load users.");

        return extract_collection(users_data).map((user) => get_user_row(user));
    },

    get_missing_user_list_message() {
        return !this.users_list_url
            ? "VUE_APP_USER_LIST_URL is not configured. Showing available local profile data."
            : "No auth token found. Showing available local profile data.";
    },

    get_fallback_users() {
        return this.user_profile ? [get_user_row(this.user_profile)] : [];
    },

    get_fallback_role_options() {
        const current_user_role = this.user_profile?.user_role?.role || null;
        return current_user_role ? [current_user_role] : [];
    },

    get_user_payload(form_payload) {
        const role_id = form_payload.role_id ?? get_role_id(form_payload.role);
        return build_user_payload(form_payload, role_id, this.client_app);
    },

    async add_user(payload) {
        await this.save_user(payload);
    },

    async save_user(form_payload) {
        const validation_error = this.validate_user_payload(form_payload);

        if (validation_error) {
            this.form_error = validation_error;
            return;
        }

        await this.persist_valid_user(form_payload);
    },

    async persist_valid_user(form_payload) {
        this.start_user_save();

        try {
            const payload = this.get_user_payload(form_payload);
            await this.save_user_payload(form_payload, payload);
            this.save_success = "User added successfully.";
            this.close_add_user_modal();
        } catch (error) {
            this.form_error = error.message || "Unable to save user.";
        } finally {
            this.is_saving = false;
        }
    },

    start_user_save() {
        this.is_saving = true;
        this.form_error = "";
        this.save_success = "";
        this.destroy_tables();
    },

    async save_user_payload(form_payload, payload) {
        if (!this.create_user_url) {
            this.save_user_locally(form_payload, payload);
            return;
        }

        const request = new Request(this.create_user_url, {
            method: "POST",
            headers: get_auth_headers(true),
            body: JSON.stringify(payload)
        });
        const response = await send_request(request);

        await get_response_data(response, "Unable to save user.");
        await this.load_users();
    },

    get_activate_user_endpoint(user_id) {
        return get_status_endpoint(this.users_url, user_id, "update");
    },

    get_deactivate_user_endpoint(user_id) {
        return get_status_endpoint(this.deactivate_user_url, user_id, "deactivate");
    },

    get_user_status_endpoint(user, next_user) {
        const method_name = next_user.is_active ? "get_activate_user_endpoint" : "get_deactivate_user_endpoint";
        return user.id ? this[String(method_name)](user.id) : "";
    },

    get_user_status_payload(next_user) {
        return next_user.is_active ? { is_active: next_user.is_active } : null;
    },

    replace_user_in_list(user, next_user) {
        return this.users.map((existing_user) => {
            return String(existing_user.id) === String(user.id) ? next_user : existing_user;
        });
    },

    async save_user_status_to_server(endpoint, next_user) {
        const payload = this.get_user_status_payload(next_user);
        const request = new Request(endpoint, {
            method: "POST",
            headers: get_auth_headers(true),
            body: payload ? JSON.stringify(payload) : null
        });
        const response = await send_request(request);

        await get_response_data(response, "Unable to update user status.");
    },

    is_current_user(user) {
        return is_matching_user_id(this, user) || is_matching_user_email(this, user);
    }
};
